import React, { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { Image as ImageIcon, X } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // image object

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/gallery");
        if (!cancelled) setItems(res.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#d4af37]" />
              <span className="text-xs uppercase tracking-[0.3em] text-[#d4af37] font-bold">
                Gallery
              </span>
            </div>
            <h2 className="font-heading text-5xl md:text-6xl uppercase leading-[0.9]">
              Inside <br /> <span className="text-[#d4af37]">The Gym.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-sm text-sm md:text-base">
            Rounds, rolls and fight nights. Shots from the mat and the cage.
          </p>
        </div>

        {loading ? (
          <div className="text-white/50 text-sm uppercase tracking-[0.2em]">Loading…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="gallery-empty"
            className="border border-dashed border-white/15 p-12 text-center"
          >
            <ImageIcon className="mx-auto text-[#d4af37] mb-4" size={32} />
            <div className="font-heading text-2xl uppercase">Gallery coming soon</div>
            <p className="mt-2 text-sm text-white/60">
              Photos from training and fight nights will be posted here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {items.map((img) => (
              <button
                key={img.id}
                data-testid={`gallery-thumb-${img.id}`}
                onClick={() => setLightbox(img)}
                className="group relative aspect-square overflow-hidden bg-[#111] border border-white/10 hover:border-[#d4af37]/60 transition-colors"
              >
                <img
                  src={`${API}/gallery/image/${img.id}`}
                  alt={img.caption || "Full Circle MMA training"}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                {img.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-xs text-white/85 line-clamp-2">
                      {img.caption}
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="gallery-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 w-10 h-10 border border-white/20 flex items-center justify-center text-white hover:border-[#d4af37] hover:text-[#d4af37]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div
            className="max-w-6xl max-h-[88vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${API}/gallery/image/${lightbox.id}`}
              alt={lightbox.caption || "Full Circle MMA"}
              className="max-h-[88vh] max-w-full object-contain"
            />
            {lightbox.caption && (
              <div className="mt-4 text-center text-white/70 text-sm">
                {lightbox.caption}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
